import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { PageHeader } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { eur, CURRENCIES, catLabel } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { t } from "@/i18n";
import { SubscriptionCard } from "@/components/billing/SubscriptionCard";

export default function Settings() {
  const { user, loadMe } = useAuth();
  const [name, setName] = useState("");
  const [target, setTarget] = useState(30);
  const [currency, setCur] = useState("EUR");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.restaurant) {
      setName(user.restaurant.name);
      setTarget(user.restaurant.target_food_cost || 30);
      setCur(user.restaurant.currency || "EUR");
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error(t("settings.le_nom_du_restaurant_est_obligatoire"));
    setBusy(true);
    try {
      await api.put("/restaurant", { name: name.trim(), target_food_cost: Number(target), currency });
      await loadMe();
      toast.success(t("settings.parametres_enregistres"));
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const example = 4.5;
  return (
    <>
      <PageHeader eyebrow={t("settings.votre_restaurant")} title={t("common.parametres")} description={t("settings.ces_reglages_s_appliquent_aux_calculs_de_r")} />
      <div className="mb-6"><SubscriptionCard /></div>
      <form onSubmit={save} className="max-w-2xl space-y-6" data-testid="settings-form">
        <div className="space-y-2 rounded-2xl border bg-card p-6">
          <Label htmlFor="rname">{t("common.nom_du_restaurant")}</Label>
          <Input id="rname" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-restaurant-name-input" />
        </div>
        <div className="flex flex-col gap-3 rounded-2xl border bg-card p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Label>{t("settings.currency")}</Label>
            <p className="mt-1 text-xs text-muted-foreground">{t("settings.currency_hint")}</p>
          </div>
          <Select value={currency} onValueChange={setCur}>
            <SelectTrigger className="sm:w-32" data-testid="settings-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-5 rounded-2xl border bg-card p-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <Label>{t("settings.objectif_de_cout_matiere")}</Label>
              <p className="mt-1 text-xs text-muted-foreground">{t("settings.seuil_au_dela_duquel_un_plat_est_juge_trop")}</p>
            </div>
            <div className="relative w-24">
              <Input className="num pr-7 text-right" type="number" min={5} max={80} step={0.5} value={target}
                onChange={(e) => setTarget(e.target.value)} data-testid="settings-target-input" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <Slider min={15} max={50} step={0.5} value={[Number(target) || 30]} onValueChange={([v]) => setTarget(v)} data-testid="settings-target-slider" />
          <p className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground" data-testid="settings-target-example">
            {t("settings.exemple_un_plat_qui_coute")} <span className="num text-foreground">{eur(example)}</span> {t("settings.devrait_etre_vendu_au_moins")} <span className="num font-semibold text-foreground">{eur(example / ((Number(target) || 30) / 100))}</span> {t("common.ht_short")}.
          </p>
        </div>
        <Button type="submit" disabled={busy} data-testid="settings-save-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{t("common.enregistrer")}
        </Button>
      </form>
    </>
  );
}
